/**
 * ViewerOverviewStats — the read-only seat's at-a-glance numbers.
 *
 * Four tiles (contacts, companies, open deals, pipeline value), each a link
 * into the list it counts. Rendered from the dashboard metrics the page has
 * already loaded — no extra requests — so a viewer lands on the shape of the
 * business, not on an empty chat box.
 */

import { Link } from 'react-router-dom';

import { formatCurrency } from '@/pages/dashboard/useDashboardMetrics';

import type { DashboardMetricsData } from '../hooks/useDashboardData';

interface ViewerOverviewStatsProps {
  metrics: DashboardMetricsData;
  orgId: number;
}

interface StatTile {
  key: string;
  label: string;
  value: string;
  href: string;
}

const TILE_CLASS =
  'block rounded-[4px] border border-secondary bg-primary p-4 transition-colors hover:bg-primary_hover outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2';

function StatTileLink({ tile }: { tile: StatTile }) {
  return (
    <Link
      to={tile.href}
      className={TILE_CLASS}
      aria-label={`${tile.label}: ${tile.value}`}
    >
      <div className='text-[13px] font-semibold text-tertiary'>
        {tile.label}
      </div>
      <div className='mt-1 text-[18px] font-semibold text-primary'>
        {tile.value}
      </div>
    </Link>
  );
}

function formatCount(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

export function ViewerOverviewStats({
  metrics,
  orgId,
}: ViewerOverviewStatsProps) {
  const dash = '—';
  const loading = metrics.isLoading;
  const tiles: StatTile[] = [
    {
      key: 'contacts',
      label: 'Contacts',
      value: loading ? dash : formatCount(metrics.contactsCount),
      href: `/${orgId}/crm/contacts`,
    },
    {
      key: 'companies',
      label: 'Companies',
      value: loading ? dash : formatCount(metrics.companiesCount),
      href: `/${orgId}/crm/companies`,
    },
    {
      key: 'open-deals',
      label: 'Open Deals',
      value: loading ? dash : formatCount(metrics.activeDealCount),
      href: `/${orgId}/crm/deals`,
    },
    {
      key: 'pipeline-value',
      label: 'Pipeline Value',
      value: loading
        ? dash
        : formatCurrency(metrics.totalPipelineValue, metrics.currencyCode),
      href: `/${orgId}/crm/deals`,
    },
  ];

  return (
    <section aria-label='Overview' aria-busy={loading} className='space-y-3'>
      <h2 className='text-[14px] font-semibold text-primary'>Overview</h2>
      <div className='grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4'>
        {tiles.map(tile => (
          <StatTileLink key={tile.key} tile={tile} />
        ))}
      </div>
    </section>
  );
}
